import { oublierAvis } from './avis'
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { Session } from '@supabase/supabase-js'
import { supabase } from './supabase'
import type { Profil } from './types'

interface ContexteAuth {
  session: Session | null
  profil: Profil | null
  chargement: boolean
  deconnexion: () => Promise<void>
}

const Contexte = createContext<ContexteAuth>({
  session: null,
  profil: null,
  chargement: true,
  deconnexion: async () => {}
})

export function FournisseurAuth({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [profil, setProfil] = useState<Profil | null>(null)
  const [chargement, setChargement] = useState(true)

  useEffect(() => {
    let vivant = true

    supabase.auth.getSession().then(({ data }) => {
      if (!vivant) return
      setSession(data.session)
      if (!data.session) setChargement(false)
    })

    const { data: ecoute } = supabase.auth.onAuthStateChange((_e, s) => {
      setSession(s)
      if (!s) {
        setProfil(null)
        setChargement(false)
      }
    })

    return () => {
      vivant = false
      ecoute.subscription.unsubscribe()
    }
  }, [])

  // Le profil est relu seulement quand l'utilisateur change. Supabase
  // renouvelle la session (nouvel objet) au retour sur l'onglet du
  // navigateur et toutes les heures : sans cette garde, l'écran
  // « Chargement… » remplaçait l'application et on perdait la page
  // consultée (retour à l'onglet Info du chantier).
  const utilisateurId = session?.user.id ?? null
  useEffect(() => {
    if (!utilisateurId) return
    let vivant = true
    setChargement(true)
    supabase
      .from('profils')
      .select('id, nom, courriel, role, actif')
      .eq('id', utilisateurId)
      .single()
      .then(({ data }) => {
        if (!vivant) return
        setProfil((data as Profil) ?? null)
        setChargement(false)
      })
    return () => {
      vivant = false
    }
  }, [utilisateurId])

  return (
    <Contexte.Provider
      value={{
        session,
        profil,
        chargement,
        deconnexion: async () => {
          oublierAvis()
          await supabase.auth.signOut()
        }
      }}
    >
      {children}
    </Contexte.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): ContexteAuth {
  return useContext(Contexte)
}

/** Droits d'écriture : chef d'équipe ou administrateur, sur un chantier non archivé. */
export function peutEcrire(profil: Profil | null, statutChantier: string | undefined): boolean {
  if (!profil || !profil.actif) return false
  if (statutChantier === 'termine') return false
  return profil.role === 'chef' || profil.role === 'admin'
}

export function estAdmin(profil: Profil | null): boolean {
  return profil?.actif === true && profil.role === 'admin'
}
